/**
 * Which internal paths sit OUTSIDE the `[locale]` segment.
 *
 * The app has two kinds of internal route. Marketing and public reports live
 * under `app/(marketing)/[locale]` and `app/(reports)/[locale]`, so their URLs
 * carry a locale prefix. Everything in `app/(saas)` (`/app`, `/auth/*`), the
 * design lab, the API and the image proxy do not — `middleware.ts` handles
 * those branches before `intlMiddleware` ever runs.
 *
 * `LocaleLink` is configured with `localePrefix: "always"`, so it prefixes
 * every href it is handed. Pointing it at one of the paths below yields
 * `/en/auth/signup`, which no longer matches middleware's `/auth` branch,
 * falls through to next-intl, and 404s inside the marketing group. Nothing
 * warns: it type-checks, builds, renders, and only breaks when clicked.
 *
 * Hence `SiteLink`, which routes these through plain `next/link` instead.
 */
const UNLOCALIZED_PREFIXES = [
	"/app",
	"/auth",
	"/design",
	"/api",
	"/image-proxy",
] as const;

/**
 * True when `href` is an internal path that must NOT receive a locale prefix.
 *
 * Anything that is not a root-relative path — an absolute URL, `mailto:`, a
 * bare `#hash` — is not ours to prefix either, but `LocaleLink` already leaves
 * those alone, so they answer `false` and keep their existing behaviour.
 */
export function isUnlocalizedPath(href: string): boolean {
	if (!href.startsWith("/")) {
		return false;
	}

	return UNLOCALIZED_PREFIXES.some(
		(prefix) =>
			href === prefix ||
			href.startsWith(`${prefix}/`) ||
			href.startsWith(`${prefix}?`) ||
			href.startsWith(`${prefix}#`),
	);
}
